import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import * as objectPath from 'object-path';
import {LayoutConfigModel} from '../models/layout-config.model';
import { LayoutConfig } from '../../../_config/layout.config';
import { objectMerge } from '../../../helpers/object.helper';

@Injectable()
export class LayoutConfigService {
    // Public properties
    onConfigUpdated$: Subject<LayoutConfigModel>;
    layoutConfig: LayoutConfigModel;

    /**
     * Servcie constructor
     */
    constructor() {
        // register on config changed event and set default config
        this.onConfigUpdated$ = new Subject();
        this.layoutConfig = this.getSavedConfig();
    }

    /**
     * Save layout config to the local storage
     */
    saveConfig(layoutConfig: LayoutConfigModel): void {
        this.layoutConfig = layoutConfig;
        localStorage.setItem('layoutConfig', JSON.stringify(layoutConfig));
    }

    /**
     * Get layout config from local storage
     */
    getSavedConfig(): LayoutConfigModel {
        const config = localStorage.getItem('layoutConfig');
        if (!config) {
            return new LayoutConfig().configs;;
        }
        try {
            return JSON.parse(config);
        } catch (e) {
            console.error(e);
            return new LayoutConfig().configs;;
        }
    }

    resetConfig(): void {
        localStorage.removeItem('layoutConfig');
        this.layoutConfig = new LayoutConfig().configs;
    }

    getConfig(): LayoutConfigModel {
        return this.layoutConfig;
    }

    setConfig(value: any, save?: boolean): void {
        objectMerge(this.layoutConfig, value);

        if (save && this.layoutConfig) {
            this.saveConfig(this.layoutConfig);
        }

        // fire off an event that all subscribers will listen
        this.onConfigUpdated$.next(this.layoutConfig);
    }

    /**
     * Get brand logo
     */
    getLogo(): string | undefined {
        if (!this.layoutConfig) {
            return undefined;
        }
        const menuAsideLeftSkin = this.layoutConfig.brand?.self.skin;
        // set brand logo
        const logoObject = this.layoutConfig.self.logo;

        let logo: string | undefined = undefined;
        if (typeof logoObject === 'string') {
            logo = logoObject;
        }
        if (typeof logoObject === 'object' && menuAsideLeftSkin) {
            logo = objectPath.get(logoObject, menuAsideLeftSkin);
        }
        if (typeof logo === 'undefined') {
            try {
                const logos = objectPath.get(this.layoutConfig, 'self.logo');
                logo = logos[Object.keys(logos)[0]];
            } catch (e) {
                console.error(e);
            }
        }
        return logo;
    }

    /**
     * Returns sticky logo
     */
    getStickyLogo(): string | undefined {
        if (!this.layoutConfig) {
            return undefined;
        }
        let logo: string | undefined = objectPath.get(this.layoutConfig, 'self.logo.sticky');
        if (typeof logo === 'undefined') {
            logo = this.getLogo();
        }
        return logo;
    }

    /**
     * Reload current layout config to the state of latest saved config
     */
    reloadConfigs(): LayoutConfigModel {
        this.layoutConfig = this.getSavedConfig();
        this.onConfigUpdated$.next(this.layoutConfig);
        return this.layoutConfig;
    }

    /**
     * Get default route name by object
     */
    getCurrentMainRoute(): string {
        const config = this.getConfig();
        if (!config) {
            return '';
        }

        const url = config.demo;
        if (!url) {
            return '';
        }

        return url;
    }
}
